import { type Language, translations, type Translations } from "@/lib/translations";
import * as Sentry from "@sentry/react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("language") as Language;
        if (saved && translations[saved]) {
          return saved;
        }
      } catch (error) {
        // localStorage may throw under private browsing / quota exceeded.
        Sentry.captureException(error, {
          tags: { source: "language-context.getItem" },
        });
      }
    }
    return "en";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem("language", lang);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { source: "language-context.setItem" },
        extra: { lang },
      });
    }
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
